import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useAuthStore } from '@/store/authStore';
import { Landing } from '@/pages/Landing';
import { Aspirations } from '@/pages/Aspirations';
import { Goals } from '@/pages/Goals';
import { Lists } from '@/pages/Lists';
import { ListTemplates } from '@/pages/ListTemplates';
import { Calendar } from '@/pages/Calendar';
import { Schedules } from '@/pages/Schedules';
import { Projections } from '@/pages/Projections';
import { Reports } from '@/pages/Reports';
import { Profile } from '@/pages/Profile';
import { Settings } from '@/pages/Settings';
import { Notifications } from '@/pages/Notifications';
import { Help } from '@/pages/Help';
import { HelpArticle } from '@/pages/HelpArticle';
import { Debug } from '@/pages/Debug';
import { NotFound } from '@/pages/NotFound';
import { PrivacyPolicy } from '@/pages/PrivacyPolicy';
import { TermsOfService } from '@/pages/TermsOfService';
import AuthCallback from '@/pages/AuthCallback';
import { ResetPassword } from '@/pages/ResetPassword';
import { VerifyEmail } from '@/pages/VerifyEmail';

// Home route component that shows Landing or Aspirations based on auth state
function HomeRoute() {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Landing />;
  }

  return <Aspirations />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <NotFound />,
    children: [
      // Home route - shows Landing for unauthenticated, Aspirations for authenticated
      {
        index: true,
        element: <HomeRoute />,
      },
      // Protected routes - require authentication
      {
        path: 'aspirations',
        element: (
          <ProtectedRoute requireAuth feature="Aspirations">
            <Aspirations />
          </ProtectedRoute>
        ),
      },
      {
        path: 'goals',
        element: (
          <ProtectedRoute requireAuth feature="Goals">
            <Goals />
          </ProtectedRoute>
        ),
      },
      {
        path: 'lists',
        element: (
          <ProtectedRoute requireAuth feature="Lists">
            <Lists />
          </ProtectedRoute>
        ),
      },
      {
        path: 'list-items/:listId',
        element: (
          <ProtectedRoute requireAuth feature="Lists">
            <Lists />
          </ProtectedRoute>
        ),
      },
      {
        path: 'list-templates',
        element: (
          <ProtectedRoute requireAuth feature="List Templates">
            <ListTemplates />
          </ProtectedRoute>
        ),
      },
      {
        path: 'list-templates/:templateId',
        element: (
          <ProtectedRoute requireAuth feature="List Templates">
            <ListTemplates />
          </ProtectedRoute>
        ),
      },
      {
        path: 'calendar',
        element: (
          <ProtectedRoute requireAuth feature="Calendar">
            <Calendar />
          </ProtectedRoute>
        ),
      },
      {
        path: 'schedules',
        element: (
          <ProtectedRoute requireAuth feature="Schedules">
            <Schedules />
          </ProtectedRoute>
        ),
      },
      {
        path: 'projections',
        element: (
          <ProtectedRoute requireAuth feature="Projections">
            <Projections />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requireAuth feature="Reports">
            <Reports />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute requireAuth feature="your Profile">
            <Profile />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute requireAuth feature="Settings">
            <Settings />
          </ProtectedRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ProtectedRoute requireAuth feature="Notifications">
            <Notifications />
          </ProtectedRoute>
        ),
      },
      {
        path: 'debug',
        element: (
          <ProtectedRoute requireAuth feature="Debug tools">
            <Debug />
          </ProtectedRoute>
        ),
      },
      // Public routes - no authentication required
      {
        path: 'help',
        element: <Help />,
      },
      {
        path: 'help/article/:slug',
        element: <HelpArticle />,
      },
      {
        path: 'about',
        element: <div className="p-8">About page coming soon...</div>,
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'terms',
        element: <TermsOfService />,
      },
    ],
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
